import React, { useEffect, useState } from "react";
import api, { describeError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const blank = { case_id: "", court: "", date: "", time: "", court_room: "", purpose: "", notes: "", status: "scheduled", advocate_id: "" };

export default function HearingDialog({ open, onOpenChange, caseId, initial, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState({ ...blank, case_id: caseId || "" });
  const [cases, setCases] = useState([]);
  const [advocates, setAdvocates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...blank, ...initial } : { ...blank, case_id: caseId || "" });
      api.get("/advocates").then(({ data }) => setAdvocates(data)).catch(() => {});
      if (!caseId && !editing) api.get("/cases").then(({ data }) => setCases(data)).catch(() => {});
    }
  }, [open, caseId, initial, editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    if (!form.case_id || !form.date) { toast.error("Case and date are required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/hearings/${initial.id}`, form);
        toast.success("Hearing updated");
      } else {
        await api.post("/hearings", form);
        toast.success("Hearing scheduled");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(describeError(err, "Could not save the hearing.")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl text-navy">{editing ? "Edit Hearing" : "Schedule Hearing"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {!caseId && !editing && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Case</Label>
              <Select value={form.case_id} onValueChange={set("case_id")}>
                <SelectTrigger data-testid="hearing-case-select"><SelectValue placeholder="Select case" /></SelectTrigger>
                <SelectContent>{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Date</Label><Input type="date" data-testid="hearing-date-input" value={form.date} onChange={set("date")} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Time</Label><Input type="time" value={form.time} onChange={set("time")} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Court</Label><Input value={form.court} onChange={set("court")} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Court Room</Label><Input value={form.court_room} onChange={set("court_room")} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Purpose</Label><Input value={form.purpose} onChange={set("purpose")} placeholder="Arguments / Evidence" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Advocate</Label>
              <Select value={form.advocate_id || ""} onValueChange={set("advocate_id")}>
                <SelectTrigger data-testid="hearing-advocate-select"><SelectValue placeholder="Assign advocate" /></SelectTrigger>
                <SelectContent>{advocates.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger data-testid="hearing-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="adjourned">Adjourned</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="hearing-save-btn" className="bg-navy text-white hover:bg-navy-light">{saving ? "Saving…" : editing ? "Update Hearing" : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
